import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { ROLES } from '../constants/roles';
import { authService } from '../services/api';

const AuthContext = createContext(null);

const STORAGE_KEY = 'medihub_admin_auth';
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 2 minutes
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export const ROLE_DEFAULT_PATH = {
  [ROLES.ADMIN]:    '/',
  [ROLES.LAB]:      '/lab-bookings',
  [ROLES.PHARMACY]: '/inventory',
  [ROLES.SUPPORT]:  '/support',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (!parsed.roleId) { localStorage.removeItem(STORAGE_KEY); return null; }
      return parsed;
    } catch { return null; }
  });

  const login = useCallback(async (email, password) => {
    try {
      const res = await authService.login(email, password);
      if (!res.success) return { success: false, error: res.message || 'Invalid email or password' };

      const { token, user: u } = res.data;

      const userData = {
        id:          u.id,
        email:       u.email,
        name:        u.name,
        roleId:      u.roleId,
        roleLabel:   u.roleLabel,
        permissions: u.permissions, // { lab_tests: ['read','create'], ... }
      };

      localStorage.setItem('token', token);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
      setUser(userData);

      const path = u.default_route || ROLE_DEFAULT_PATH[u.roleId] || '/';
      return { success: true, roleId: u.roleId, path };
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Login failed';
      return { success: false, error: msg };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  // Auto logout on inactivity
  const timerRef = useRef(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      logout();
      window.location.href = '/login';
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  useEffect(() => {
    if (!user) return;
    resetTimer();
    EVENTS.forEach(e => window.addEventListener(e, resetTimer));
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      EVENTS.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user, resetTimer]);

  // permissions is { lab_tests: ['read','create'], ... }
  // permissionId format: 'lab_tests:read' or just 'lab_tests' (checks any action)
  const hasPermission = useCallback((permissionId) => {
    if (!user) return false;
    if (user.roleId === ROLES.ADMIN) return true;
    if (!user.permissions) return false;

    const [resource, action] = String(permissionId).split(':');
    const actions = user.permissions[resource];
    if (!actions) return false;
    if (!action) return actions.length > 0;
    return actions.includes(action);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
